import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import AccountChip from './components/AccountChip';
import AppHeader from './components/AppHeader';
import Footer from './components/Footer';
import FloatingLanguageSwitcher from './components/FloatingLanguageSwitcher';
import LoginDrawer from './components/LoginDrawer';
import LanguageSync from './i18n/LanguageSync';
import LanguageUrlSync from './i18n/LanguageUrlSync';
import PitchCarousel from './components/PitchCarousel';
import PostHogProvider from './components/PostHogProvider';
import RouteMeta from './components/RouteMeta';
import RequireAuth from './components/RequireAuth';
import EditorPage from './pages/EditorPage';
import DecksPage from './pages/DecksPage';
import NewDeckPage from './pages/NewDeckPage';
import PrivacyPage from './pages/PrivacyPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ProfilePage from './pages/ProfilePage';
import TemplatesPage from './pages/TemplatesPage';
import TermsPage from './pages/TermsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { migrateLegacyDesignKey } from './data/designStore';
import { DEFAULT_DESIGN_ID, DesignPalette, getDesignOrDefault } from './data/designTemplates';
import { typographyCssVariables } from './styles/typography';

const GlobalStyle = createGlobalStyle<{ $palette: DesignPalette }>`
  *, *::before, *::after {
    box-sizing: border-box;
  }

  :root {
    --pitch-blue-light: ${({ $palette }) => $palette.blueLight};
    --pitch-blue: ${({ $palette }) => $palette.blue};
    --pitch-blue-dark: ${({ $palette }) => $palette.blueDark};
    --pitch-navy: ${({ $palette }) => $palette.navy};
    --pitch-navy-deep: ${({ $palette }) => $palette.navyDeep};
    --pitch-cream: ${({ $palette }) => $palette.cream};
    --pitch-accent: ${({ $palette }) => $palette.accent};
    --pitch-text: ${({ $palette }) => $palette.text};
    --pitch-text-muted: ${({ $palette }) => $palette.textMuted};
    --pitch-border: ${({ $palette }) => $palette.border};
    --pitch-gradient: ${({ $palette }) => $palette.gradient};
    --pitch-page-gradient: ${({ $palette }) => $palette.pageGradient};
    ${typographyCssVariables()}
  }

  body {
    margin: 0;
    font-family: var(--pitch-font-ui, 'DM Sans', sans-serif);
    color: #0A2E28;
    background: #FAFAF8;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  ::selection {
    background: #E8F5F0;
    color: #0A2E28;
  }

  button:focus-visible,
  a:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid #134E44;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

const AppFrame = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProtectedDecks = () => (
  <RequireAuth next="/decks">
    <DecksPage />
  </RequireAuth>
);

const ProtectedNewDeck = () => {
  const { pathname, search } = useLocation();
  return (
    <RequireAuth next={`${pathname}${search}`}>
      <NewDeckPage />
    </RequireAuth>
  );
};

const ProtectedEditor = () => (
  <RequireAuth next="/edit">
    <EditorPage />
  </RequireAuth>
);

const ProtectedProfile = () => (
  <RequireAuth next="/profile">
    <ProfilePage />
  </RequireAuth>
);

const LegacyLoginRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { openLogin } = useAuth();

  useEffect(() => {
    const next = searchParams.get('next') || '/';
    openLogin(next === '/' ? undefined : next);
    navigate(next.startsWith('/') ? next : `/${next}`, { replace: true });
  }, [searchParams, navigate, openLogin]);

  return <TemplatesPage />;
};

const AppShell = () => {
  React.useEffect(() => {
    migrateLegacyDesignKey();
  }, []);

  const appPalette = getDesignOrDefault(DEFAULT_DESIGN_ID).palette;

  return (
    <AppFrame>
      <GlobalStyle $palette={appPalette} />
      <LanguageSync />
      <LanguageUrlSync />
      <RouteMeta />
      <AppHeader />
      <AccountChip />
      <FloatingLanguageSwitcher />
      <LoginDrawer />
      <Main>
        <Routes>
          <Route path="/" element={<TemplatesPage />} />
          <Route path="/preview" element={<PitchCarousel />} />
          <Route path="/decks" element={<ProtectedDecks />} />
          <Route path="/decks/new" element={<ProtectedNewDeck />} />
          <Route path="/edit" element={<ProtectedEditor />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/profile" element={<ProtectedProfile />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/login" element={<LegacyLoginRedirect />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
      </Main>
      <Footer />
    </AppFrame>
  );
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <PostHogProvider>
        <AppShell />
      </PostHogProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
